import { Link } from 'react-router-dom';

/**
 * Static homepage sections: what the platform does, how a submission flows, who it is for, and the
 * closing call to action.
 *
 * Every claim here describes what ships today. Where a capability is conditional (an Android tier
 * that may be detached, a macOS gap) the copy says so rather than implying uniform coverage.
 *
 * PRESENTATION NOTE. These sections used to be three grids of bordered, filled cards, two of them
 * with an icon in a tinted rounded square. That is the single most recognisable "generic SaaS
 * landing page" pattern there is, and the homepage carried 65 rounded cards because of it. They are
 * now catalogue rows: a monospace index, a hairline rule, and the text. Same words, same SEO value,
 * but the page reads as an instrument rather than a brochure, and the reader's eye follows one
 * column instead of bouncing around a tile mosaic.
 */

function SectionHeading({ title, lead, id }: { title: string; lead?: string; id?: string }) {
  return (
    <div className="mb-5">
      {/* Section headings are body-sized and separated by WEIGHT, not size. A ladder of ever-larger
          headings gives each one less authority, not more. */}
      <h2 id={id} className="text-section font-semibold text-text-primary">
        {title}
      </h2>
      {lead && <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-text-secondary">{lead}</p>}
    </div>
  );
}

/** A catalogue row: index, title, prose. No fill, no icon chip, one hairline above. */
function Entry({ n, title, children, href }: { n: string; title: string; children: React.ReactNode; href?: string }) {
  return (
    <div className="border-t border-border-subtle pt-3">
      <div className="flex items-baseline gap-2.5">
        <span className="font-mono text-micro text-text-muted">{n}</span>
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
      </div>
      <p className="mt-1.5 pl-[1.9rem] text-sm leading-relaxed text-text-secondary">{children}</p>
      {href && (
        <Link
          to={href}
          className="mt-1.5 ml-[1.9rem] inline-block text-sm font-medium text-accent-500 hover:underline"
        >
          Learn more
        </Link>
      )}
    </div>
  );
}

export function CapabilityGrid() {
  return (
    <section className="mt-12" aria-labelledby="capabilities">
      <SectionHeading
        id="capabilities"
        title="What you get on every submission"
        lead="One workspace for files, URLs and hashes, with the evidence behind every verdict kept on the report rather than summarised away."
      />
      <div className="grid gap-x-8 gap-y-4 md:grid-cols-2 lg:grid-cols-3">
        <Entry n="01" title="Multi-engine static analysis">
          Antivirus, YARA rule packs and hash reputation run over every sample, alongside deep parsers
          for PE, ELF, Mach-O, Android packages, Office documents, PDFs and email carriers. Each engine
          is listed by name with its own result, including the ones that found nothing.
        </Entry>
        <Entry n="02" title="Real sandbox detonation">
          Windows and Linux samples execute in instrumented full-system sandboxes with controlled
          network egress, recording the process tree, API activity, dropped files, memory artifacts and
          captured traffic. Behavior is never simulated, and an incomplete run is labelled as one.
        </Entry>
        <Entry n="03" title="Interactive live VM">
          Take the keyboard. Drive a sample yourself in a live desktop session when a payload needs a
          click, a password, or a decision an automated run cannot make.
        </Entry>
        <Entry n="04" title="URL and phishing analysis">
          Scan a page through an egress-locked fetcher, then render it in an isolated browser for
          requests, cookies, scripts and a screenshot. Brand impersonation, homograph names, phishing
          kits and credential-harvesting forms are called out with the reason each was flagged.
        </Entry>
        <Entry n="05" title="Threat graph and pivots">
          Move from a sample to its infrastructure, its family, the URLs it talks to and the samples
          that resemble it. Save an investigation, enrich nodes, and export the graph.
        </Entry>
        <Entry n="06" title="Built to integrate">
          A documented REST API, an OpenAPI specification, STIX and CSV feeds, a TAXII endpoint, SIEM
          and SOAR connectors, and downloadable PDF or Word reports.
        </Entry>
      </div>
    </section>
  );
}

const STEPS: { n: string; title: string; body: string }[] = [
  {
    n: '01',
    title: 'Submit',
    body: 'Drop a file, paste a URL, or look up a hash. Files are hashed in your browser first, so a sample already in the corpus returns its report immediately.',
  },
  {
    n: '02',
    title: 'Static analysis',
    body: 'The full engine roster scans the bytes while parsers pull apart the file structure, extract strings, imports, certificates and embedded indicators.',
  },
  {
    n: '03',
    title: 'Detonation',
    body: 'Executable types are queued to a sandbox guest and run for real. A submission waits for a genuine slot rather than degrading to a lesser analysis.',
  },
  {
    n: '04',
    title: 'Pivot and export',
    body: 'Read the fused verdict with its signals, follow the indicators into the graph, and export the result as a document, a STIX bundle, or an API response.',
  },
];

export function HowItWorks() {
  return (
    <section className="mt-12" aria-labelledby="how-it-works">
      <SectionHeading id="how-it-works" title="How an analysis runs" />
      <ol className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((s) => (
          <li key={s.n} className="border-t border-border-subtle pt-3">
            <div className="flex items-baseline gap-2.5">
              <span className="font-mono text-micro text-accent-500">{s.n}</span>
              <h3 className="text-sm font-semibold text-text-primary">{s.title}</h3>
            </div>
            <p className="mt-1.5 text-sm leading-relaxed text-text-secondary">{s.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

const USES: { title: string; body: string; to: string; cta: string }[] = [
  {
    title: 'Alert triage',
    body: 'Resolve an unknown binary from an endpoint alert: verdict, family attribution, indicators to search your telemetry for, and a shareable report.',
    to: '/malware-analysis',
    cta: 'Malware analysis',
  },
  {
    title: 'Phishing investigation',
    body: 'Take a reported link, see what it actually serves, whether it impersonates a brand, where it posts credentials, and which malware already references it.',
    to: '/url-scanner',
    cta: 'URL scanner',
  },
  {
    title: 'Threat hunting',
    body: 'Search the corpus by content, indicator, technique or family, save a standing hunt, and get notified when a new sample matches it.',
    to: '/threat-intelligence',
    cta: 'Threat intelligence',
  },
  {
    title: 'Malware research',
    body: 'Detonate with the guest, network mode and engine you choose, drive the sample interactively, and compare two builds side by side.',
    to: '/sandbox',
    cta: 'Sandbox',
  },
];

export function UseCases() {
  return (
    <section className="mt-12" aria-labelledby="use-cases">
      <SectionHeading id="use-cases" title="Who it is for" />
      <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
        {USES.map((u) => (
          <div key={u.title} className="border-t border-border-subtle pt-3">
            <h3 className="text-sm font-semibold text-text-primary">{u.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-text-secondary">{u.body}</p>
            <Link to={u.to} className="mt-1.5 inline-block text-sm font-medium text-accent-500 hover:underline">
              {u.cta}
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ClosingCta() {
  return (
    <section className="mt-12 border-t border-border-default pt-8">
      <h2 className="text-h3 font-bold text-text-primary">Start with a file, a URL, or a hash</h2>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-text-secondary">
        Public analysis is free and unlimited, and no account is required. Create one to analyze
        privately, use the API, and keep your submission history.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <a href="#submit" className="rounded-sm bg-cta px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-cta-hover">
          Analyze something now
        </a>
        <Link to="/docs" className="rounded-sm border border-border-default px-4 py-2 text-sm font-medium text-text-primary transition-colors hover:bg-bg-overlay">
          Read the API docs
        </Link>
        <Link to="/pricing" className="text-sm font-medium text-text-secondary hover:text-text-primary">
          See plans
        </Link>
      </div>
    </section>
  );
}
